import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { paymentAPI } from '../../services/api';
import { redirectToCheckout } from '../../services/stripeService';
import Header from '../Layout/Header';
import {
  Check,
  X,
  Zap,
  Building2,
  Rocket,
  Crown,
  Loader2,
  AlertCircle,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';

const PricingPage = () => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [plansRes, statusRes] = await Promise.all([
        paymentAPI.getPlans(),
        paymentAPI.getSubscriptionStatus()
      ]);
      setPlans(plansRes.data);
      setSubscription(statusRes.data);
    } catch (err) {
      console.error('Failed to fetch pricing data:', err);
      setError('Failed to load pricing information');
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyPayment = useCallback(async (sessionId) => {
    try {
      setLoading(true);
      await paymentAPI.verifySession(sessionId);
      setSuccessMessage('Payment successful! Your subscription has been upgraded.');
      if (refreshUser) {
        await refreshUser();
      }
      // Clear URL params
      navigate('/pricing', { replace: true });
      // Refresh data
      await fetchData();
    } catch (err) {
      console.error('Payment verification failed:', err);
      setError('Failed to verify payment. Please contact support if your card was charged.');
    } finally {
      setLoading(false);
    }
  }, [refreshUser, navigate, fetchData]);

  // Handle payment success/cancel from Stripe redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');

    if (sessionId) {
      verifyPayment(sessionId);
    } else if (canceled) {
      setError('Payment was canceled. You can try again when ready.');
    }
  }, [searchParams, verifyPayment]);

  // Fetch plans and subscription status
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubscribe = async (planName) => {
    if (planName === 'FREE') return;
    if (planName === 'ENTERPRISE') {
      window.location.href = 'mailto:enterprise@resumeai.test?subject=Enterprise Plan Inquiry';
      return;
    }

    try {
      setProcessingPlan(planName);
      setError(null);
      const response = await paymentAPI.createCheckoutSession(planName);
      await redirectToCheckout(response.data.sessionId);
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      setError(err.response?.data?.message || 'Failed to start checkout. Please try again.');
      setProcessingPlan(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      setLoading(true);
      const response = await paymentAPI.getBillingPortal();
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Failed to open billing portal:', err);
      setError('Failed to open billing portal. Please try again.');
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will continue to have access until the end of your billing period.')) {
      return;
    }

    try {
      setLoading(true);
      await paymentAPI.cancelSubscription();
      setSuccessMessage('Your subscription has been canceled. You will have access until the end of your current billing period.');
      await fetchData();
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      setError('Failed to cancel subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPlanIcon = (planName) => {
    switch (planName) {
      case 'FREE': return <Zap className="w-8 h-8" />;
      case 'PROFESSIONAL': return <Rocket className="w-8 h-8" />;
      case 'BUSINESS': return <Building2 className="w-8 h-8" />;
      case 'ENTERPRISE': return <Crown className="w-8 h-8" />;
      default: return <Zap className="w-8 h-8" />;
    }
  };

  const getPlanStyles = (planName) => {
    switch (planName) {
      case 'FREE':
        return {
          iconBg: 'bg-gray-100 text-gray-600',
          border: 'border-gray-500 ring-gray-200',
          button: 'bg-gray-600 hover:bg-gray-700'
        };
      case 'PROFESSIONAL':
        return {
          iconBg: 'bg-blue-100 text-blue-600',
          border: 'border-blue-500 ring-blue-200',
          button: 'bg-blue-600 hover:bg-blue-700'
        };
      case 'BUSINESS':
        return {
          iconBg: 'bg-purple-100 text-purple-600',
          border: 'border-purple-500 ring-purple-200',
          button: 'bg-purple-600 hover:bg-purple-700'
        };
      case 'ENTERPRISE':
        return {
          iconBg: 'bg-amber-100 text-amber-600',
          border: 'border-amber-500 ring-amber-200',
          button: 'bg-amber-600 hover:bg-amber-700'
        };
      default:
        return {
          iconBg: 'bg-gray-100 text-gray-600',
          border: 'border-gray-500 ring-gray-200',
          button: 'bg-gray-600 hover:bg-gray-700'
        };
    }
  };

  const isCurrentPlan = (planName) => {
    return subscription?.plan === planName;
  };

  const canUpgrade = (planName) => {
    const planOrder = ['FREE', 'PROFESSIONAL', 'BUSINESS', 'ENTERPRISE'];
    const currentIndex = planOrder.indexOf(subscription?.plan || 'FREE');
    const targetIndex = planOrder.indexOf(planName);
    return targetIndex > currentIndex;
  };

  if (loading && plans.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading pricing...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        {/* Page Title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Scale your hiring process with AI-powered resume screening.
            Upgrade anytime as your needs grow.
          </p>
        </div>

        {/* Messages */}
        {error && (
          <div className="max-w-2xl mx-auto mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {successMessage && (
          <div className="max-w-2xl mx-auto mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-green-700">{successMessage}</p>
            <button onClick={() => setSuccessMessage(null)} className="ml-auto text-green-600 hover:text-green-800">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Current Subscription Info */}
        {subscription && subscription.plan !== 'FREE' && (
          <div className="max-w-2xl mx-auto mb-8 p-6 bg-white rounded-xl shadow-sm border">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Subscription</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Plan:</span>
                <span className="ml-2 font-medium">{subscription.plan}</span>
              </div>
              <div>
                <span className="text-gray-500">Credits:</span>
                <span className="ml-2 font-medium">
                  {subscription.hasUnlimitedCredits ? 'Unlimited' : `${subscription.credits} remaining`}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>
                <span className={`ml-2 font-medium ${subscription.isSubscriptionActive ? 'text-green-600' : 'text-orange-600'}`}>
                  {subscription.isSubscriptionActive ? 'Active' : (subscription.isInGracePeriod ? 'Grace Period' : 'Inactive')}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Renewal:</span>
                <span className="ml-2 font-medium">
                  {subscription.daysUntilRenewal > 0 ? `In ${subscription.daysUntilRenewal} days` : 'N/A'}
                </span>
              </div>
            </div>
            <div className="mt-4 flex gap-3">
              <button
                onClick={handleManageBilling}
                className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
              >
                Manage Billing
              </button>
              {subscription.isSubscriptionActive && !subscription.cancelAtPeriodEnd && (
                <button
                  onClick={handleCancelSubscription}
                  className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition"
                >
                  Cancel Subscription
                </button>
              )}
              {subscription.cancelAtPeriodEnd && (
                <span className="px-4 py-2 text-sm text-orange-600">
                  Cancels at period end
                </span>
              )}
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const styles = getPlanStyles(plan.name);
            const isCurrent = isCurrentPlan(plan.name);
            const canUpgradeTo = canUpgrade(plan.name);

            return (
              <div
                key={plan.name}
                className={`relative bg-white rounded-2xl shadow-sm border-2 transition-all duration-300 hover:shadow-lg ${
                  isCurrent ? `${styles.border} ring-2` : 'border-gray-100'
                } ${plan.name === 'PROFESSIONAL' ? 'lg:scale-105' : ''}`}
              >
                {/* Popular Badge */}
                {plan.name === 'PROFESSIONAL' && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-blue-600 text-white text-sm font-medium px-4 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Current Plan Badge */}
                {isCurrent && (
                  <div className="absolute -top-4 right-4">
                    <span className="bg-green-600 text-white text-sm font-medium px-3 py-1 rounded-full">
                      Current
                    </span>
                  </div>
                )}

                <div className="p-6">
                  {/* Icon & Name */}
                  <div className={`inline-flex p-3 rounded-xl ${styles.iconBg} mb-4`}>
                    {getPlanIcon(plan.name)}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{plan.displayName}</h3>

                  {/* Price */}
                  <div className="mt-4 mb-6">
                    <span className="text-4xl font-bold text-gray-900">{plan.priceDisplay}</span>
                    {plan.isRecurring && <span className="text-gray-500">{plan.period}</span>}
                  </div>

                  {/* Credits Info */}
                  <div className="mb-6 p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold text-gray-900">{plan.credits}</span> screenings
                      {plan.isRecurring && '/month'}
                    </div>
                    <div className="text-sm text-gray-600">
                      Up to <span className="font-semibold text-gray-900">{plan.maxResumesPerJob}</span> resumes per job
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  {plan.isContactSales ? (
                    <button
                      onClick={() => handleSubscribe(plan.name)}
                      className="w-full py-3 px-4 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition"
                    >
                      Contact Sales
                    </button>
                  ) : isCurrent ? (
                    <button
                      disabled
                      className="w-full py-3 px-4 bg-gray-100 text-gray-500 font-medium rounded-lg cursor-not-allowed"
                    >
                      Current Plan
                    </button>
                  ) : canUpgradeTo ? (
                    <button
                      onClick={() => handleSubscribe(plan.name)}
                      disabled={processingPlan === plan.name}
                      className={`w-full py-3 px-4 ${styles.button} text-white font-medium rounded-lg transition flex items-center justify-center gap-2 disabled:opacity-70`}
                    >
                      {processingPlan === plan.name ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        `Upgrade to ${plan.displayName}`
                      )}
                    </button>
                  ) : (
                    <button
                      disabled
                      className="w-full py-3 px-4 bg-gray-100 text-gray-400 font-medium rounded-lg cursor-not-allowed"
                    >
                      {plan.name === 'FREE' ? 'Free Forever' : 'Downgrade Not Available'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ or Additional Info */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto grid gap-4 text-left">
            <div className="p-4 bg-white rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900">What happens when I upgrade?</h3>
              <p className="text-gray-600 mt-1">
                You'll be charged immediately and your credits will be updated instantly.
                Your subscription renews monthly on the same date.
              </p>
            </div>
            <div className="p-4 bg-white rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900">Can I cancel anytime?</h3>
              <p className="text-gray-600 mt-1">
                Yes! You can cancel your subscription at any time. You'll continue to have access
                until the end of your current billing period.
              </p>
            </div>
            <div className="p-4 bg-white rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900">What payment methods do you accept?</h3>
              <p className="text-gray-600 mt-1">
                We accept all major credit cards (Visa, Mastercard, American Express) through Stripe,
                our secure payment processor.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
